import { useCallback, useState } from 'react';
import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import type { AxiosResponse, AxiosError } from 'axios';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import { useTranslation } from 'react-i18next';
import type { Environment } from '@data/fms/environment/types';
import { environmentAtom } from '@data/fms/environment/states';
import { useAtomCallback } from 'jotai/utils';

type AreaMapVersionIdBody = {
  path: '/area_map_version_id';
  value: string;
};

type NameBody = {
  path: '/environment_name';
  value: string;
};

type DescriptionBody = {
  path: '/description';
  value: string;
};

type Body = AreaMapVersionIdBody | NameBody | DescriptionBody;

/**
 * 指定走行環境の情報更新API
 * - 走行環境名変更
 * - 走行環境説明文変更
 * - エリアマップバージョン変更
 */
export const usePatchEnvironmentAPI = (): {
  state: APIState;
  patchEnvironmentAreaMapVersionId: (
    versionId: string,
  ) => Promise<Environment | null>;
  patchEnvironmentName: (name: string) => Promise<Environment | null>;
  patchEnvironmentDescription: (
    description: string,
  ) => Promise<Environment | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const patchEnvironmentAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      body: Body,
    ): Promise<Result<Environment, AxiosResponse>> => {
      try {
        const res = await fmsAPI.patch(
          `/${projectId}/environments/${environmentId}`,
          [
            {
              ...body,
              op: 'replace',
            },
          ],
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  /**
   * エリアマップバージョン変更
   */
  const patchEnvironmentAreaMapVersionId = useAtomCallback(
    useCallback(
      async (get, _, versionId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.patch_environment.area_map_version', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        const res = await patchEnvironmentAPI(
          project!.id,
          environment!.environment_id,
          {
            path: '/area_map_version_id',
            value: versionId,
          },
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.patch_environment.area_map_version', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        notifySuccess({
          message: t('api.fms.patch_environment.area_map_version', {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, patchEnvironmentAPI, t, getErrorMessage],
    ),
  );

  /**
   * 走行環境名変更
   */
  const patchEnvironmentName = useAtomCallback(
    useCallback(
      async (get, _, name: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.patch_environment.name', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        const res = await patchEnvironmentAPI(
          project!.id,
          environment!.environment_id,
          {
            path: '/environment_name',
            value: name,
          },
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.patch_environment.name', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        notifySuccess({
          message: t('api.fms.patch_environment.name', {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, patchEnvironmentAPI, t, getErrorMessage],
    ),
  );

  /**
   * 走行環境説明変更
   */
  const patchEnvironmentDescription = useAtomCallback(
    useCallback(
      async (get, _, description: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.patch_environment.description', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        const res = await patchEnvironmentAPI(
          project!.id,
          environment!.environment_id,
          {
            path: '/description',
            value: description,
          },
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.patch_environment.description', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        notifySuccess({
          message: t('api.fms.patch_environment.description', {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, patchEnvironmentAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    patchEnvironmentAreaMapVersionId,
    patchEnvironmentName,
    patchEnvironmentDescription,
  };
};
